/**
 * Active Users Dashboard Utilities
 * Shared constants, types, and helper functions for the Active Users module
 */

// ==================== CONSTANTS ====================

export const CHART_COLORS = {
	current: 'hsl(262.1 83.3% 57.8%)', // violet
	comparison: 'hsl(24.6 95% 53.1%)', // orange
} as const

// ==================== TYPES ====================

export type LegendItem = {
	color: string
	label: string
}

export type PeakLowData = {
	peak: { label: string; value: number; raw: unknown }
	low: { label: string; value: number; raw: unknown }
}

// ==================== FORMAT FUNCTIONS ====================

/**
 * Format number as Thai locale with "คน" suffix
 * @example formatUserCount(1234) => "1,234 คน"
 */
export const formatUserCount = (value: number): string =>
	Math.round(value).toLocaleString('th-TH') + ' คน'

/**
 * Format hour as HH:00 format
 * @example formatHour(9) => "09:00"
 */
export const formatHour = (hour: number): string =>
	hour.toString().padStart(2, '0') + ':00'

/**
 * Format number as Thai locale
 * @example formatNumber(1234.56) => "1,235"
 */
export const formatNumber = (value: number): string =>
	Math.round(value).toLocaleString('th-TH')

// ==================== HELPER FUNCTIONS ====================

/**
 * Find peak item in array by average property
 */
export const findPeak = <T extends { average: number }>(
	items: T[],
): T | undefined => {
	if (!items || items.length === 0) return undefined
	return items.reduce((max, curr) => (curr.average > max.average ? curr : max))
}

/**
 * Find lowest item in array by average property
 */
export const findLowest = <T extends { average: number }>(
	items: T[],
): T | undefined => {
	if (!items || items.length === 0) return undefined
	return items.reduce((min, curr) => (curr.average < min.average ? curr : min))
}
